import { ExportAllListActions } from '../ExportAllButton';
import React, { useEffect } from 'react';
import { transformCourseCityDates } from '../../helper/transform';
import {
  ListGuesser,
  ShowGuesser,
  CreateGuesser,
  FieldGuesser,
  InputGuesser,
  EditGuesser,
} from '@api-platform/admin';
import {
  DateField,
  List,
  EditButton,
  BooleanInput,
  TextField,
  ReferenceField,
  ReferenceInput,
  AutocompleteInput,
  SingleFieldList,
  Datagrid,
  useRecordContext,
  TextInput,
  ReferenceArrayField,
  ChipField,
  ShowButton,
  TopToolbar,
  SelectInput,
  FunctionField,
  useRedirect,
  useGetIdentity,
} from 'react-admin';
import { Button } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Link, useParams } from 'react-router-dom';
import { CourseTypesList } from '../../constants/Course';
import { isGranted } from '../../helper/security';
import { ROLES } from '../../constants/Roles';
import { FormTypesList } from '../../constants/CourseCity';
import { getSelectedCompanyFilter } from '../../helper/filter';

export const CourseCityList = (props) => {
  const courseCityFilters = [
    <TextInput label="City" source="city.name" />,
    <TextInput label="Course" source="course.code" />,
    <BooleanInput label="On Demand" source="onDemand" />,
    <SelectInput
      label={'Course Type'}
      source="course.type"
      choices={CourseTypesList}
    />,
    <SelectInput
      label={'FormType'}
      source="formType"
      choices={FormTypesList}
    />,
  ];

  const companyFilter = getSelectedCompanyFilter('course');

  return (
    <ListGuesser
      actions={<ExportAllListActions />}
      {...props}
      filters={courseCityFilters}
      filter={companyFilter}
    >
      <ReferenceField
        label="Course"
        source="course"
        sortBy={'course.code'}
        reference="courses"
        link="show"
      >
        <TextField source="code" />
      </ReferenceField>
      <ReferenceField
        label="City"
        source="city"
        sortBy={'city.name'}
        reference="cities"
        link="show"
      >
        <TextField source="view" />
      </ReferenceField>
      <FieldGuesser source={'onDemand'} />
      <ReferenceField
        label="Course Type"
        source="course"
        sortBy={'course.type'}
        reference="courses"
        link={false}
      >
        <FunctionField
          source={'type'}
          render={(record) =>
            CourseTypesList.filter((item) => item.id === record.type)[0].name
          }
        />
      </ReferenceField>
      <FunctionField
        source={'formType'}
        render={(record) =>
          FormTypesList.filter((item) => item.id === record.formType)[0].name
        }
      />
      <ReferenceField
        label="CreatedBy"
        source="createdBy"
        sortBy={'createdBy.email'}
        reference="users"
      >
        <TextField source="email" />
      </ReferenceField>
      <DateField source={'updatedAt'} locales={'de'} showTime />
      <DateField source={'createdAt'} locales={'de'} showTime />
    </ListGuesser>
  );
};

export const CourseCityShow = (props) => {
  const { data } = useGetIdentity();

  return (
    <ShowGuesser
      {...props}
      actions={
        isGranted(ROLES.ROLE_BRANCH_ADMIN, data) ? (
          <CourseCityActions isOnEditPage={false} />
        ) : null
      }
    >
      <TextField source={'id'} />
      <ReferenceField label="Course" source="course" reference="courses">
        <TextField source="code" />
      </ReferenceField>
      <ReferenceField label="City" source="city" reference="cities">
        <TextField source="view" />
      </ReferenceField>
      <FieldGuesser source={'drupalId'} />
      <FieldGuesser source={'oldId'} />
      <FieldGuesser source={'formType'} />
      <ReferenceArrayField
        label="Dates"
        source="dates"
        reference="course_city_dates"
      >
        <SingleFieldList>
          <ChipField source="dateView" />
        </SingleFieldList>
      </ReferenceArrayField>
      <FieldGuesser source={'createdAt'} />
      <FieldGuesser source={'updatedAt'} />
      <ReferenceField label="CreatedBy" source="createdBy" reference="users">
        <TextField source="email" />
      </ReferenceField>
      <ReferenceField label="UpdatedBy" source="updatedBy" reference="users">
        <TextField source="email" />
      </ReferenceField>
    </ShowGuesser>
  );
};

export const CourseCityCreate = (props) => (
  <CreateGuesser {...props}>
    <InputGuesser source={'course'} />
    <ReferenceInput
      source="city"
      reference="cities"
      label="City"
      filterToQuery={(searchText) => ({ name: searchText })}
    >
      <AutocompleteInput
        optionValue="@id"
        optionText={(record) => record.name}
      />
    </ReferenceInput>
  </CreateGuesser>
);

const CourseCityActions = ({ isOnEditPage }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <TopToolbar>
      {isOnEditPage ? <ShowButton /> : <EditButton />}
      <Button
        startIcon={<CalendarMonthIcon />}
        component={Link}
        color="primary"
        size={'small'}
        to={`/course_cities/${encodeURIComponent(record.id)}/calendar`}
        sx={{ lineHeight: '1.5' }}
      >
        Calendar
      </Button>
    </TopToolbar>
  );
};

export const CourseCityEdit = (props) => {
  return (
    <EditGuesser
      {...props}
      actions={<CourseCityActions isOnEditPage={true} />}
      transform={(data) => transformCourseCityDates(data)}
    >
      <CourseCityEditInputs />
    </EditGuesser>
  );
};

export const CourseCityEditInputs = (props) => {
  const { data, isLoading, error } = useGetIdentity();
  const record = useRecordContext();
  const redirect = useRedirect();

  if (!record) {
    return null;
  }

  useEffect(() => {
    if (!isGranted(ROLES.ROLE_ADMIN, data)) {
      redirect('show', 'course_cities', record.id);
    }
  }, [record, data]);

  return (
    <>
      <InputGuesser source={'onDemand'} id="onDemand" />
      <SelectInput
        required
        source="formType"
        id="formType"
        choices={FormTypesList}
      />
    </>
  );
};

export const CourseCityDatesEdit = () => {
  const { id } = useParams();

  return (
    <List
      actions={<ExportAllListActions />}
      filter={{ courseCity: id }}
      resource={'course_city_dates'}
    >
      <Datagrid>
        <ReferenceField label="Course" source="course" reference="courses">
          <TextField source="code" />
        </ReferenceField>
        <ReferenceField label="City" source="city" reference="cities">
          <TextField source="view" />
        </ReferenceField>
        <DateField source={'startDate'} locales={'de'} />
        <DateField source={'endDate'} locales={'de'} />
        <DateField source={'showUntil'} locales={'de'} />
        <FieldGuesser source={'drupalId'} />
        <FieldGuesser source={'guaranteedDate'} />
        <ReferenceField label="CreatedBy" source="createdBy" reference="users">
          <TextField source="email" />
        </ReferenceField>
        <FieldGuesser source={'createdAt'} />
        <ShowButton />
        <EditButton />
      </Datagrid>
    </List>
  );
};
